import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { StatusChip } from '../ui';
import { formatDate } from '../../lib/formatDate';
import { MockupEvent, MockupTag, asFlag, isTestTemplate, mockupEventId, mockupStatus, safeHref, sortMockupTags } from '../../lib/mockupEvents';
import { useStyles } from './styles';

export interface MockupEventCardProps {
    event: MockupEvent;
    /** Render the recognition tags under the image. */
    showTags?: boolean;
    onInfo?: (event: MockupEvent) => void;
    /** Shown (on hover, always on touch screens) when the customer has more uploads. */
    onHistory?: (customerId: string | number) => void;
    className?: string;
}

const TAG_PREVIEW = 8;

const TagStrip = ({ tags, classes }: { tags: MockupTag[] | null | undefined; classes: ReturnType<typeof useStyles> }) => {
    const [expanded, setExpanded] = useState(false);
    const sorted = useMemo(() => sortMockupTags(tags), [tags]);
    if (sorted.length === 0) return <div className={classes.tagsEmpty}>No tags</div>;
    const shown = expanded ? sorted : sorted.slice(0, TAG_PREVIEW);
    const rest = sorted.length - shown.length;
    return (
        <div className={classes.tags}>
            {shown.map((tag, index) => {
                const visible = asFlag(tag.visible);
                return (
                    <span
                        key={`${tag.tagId}-${index}`}
                        className={classNames(classes.tag, visible ? classes.tagVisible : classes.tagHidden)}
                        title={visible ? tag.tagName : `${tag.tagName} (hidden from the customer)`}
                    >
                        {tag.tagName}
                    </span>
                );
            })}
            {rest > 0 && (
                <button type="button" className={classNames(classes.tag, classes.tagMore)} onClick={() => setExpanded(true)} aria-label={`Show ${rest} more tags`}>
                    +{rest}
                </button>
            )}
            {expanded && sorted.length > TAG_PREVIEW && (
                <button type="button" className={classNames(classes.tag, classes.tagMore)} onClick={() => setExpanded(false)}>
                    Less
                </button>
            )}
        </div>
    );
};

/** One upload in the preview grids: the mockup, cart / order badges, details and history actions, optional tags. */
const MockupEventCard = ({ event, showTags = false, onInfo, onHistory, className }: MockupEventCardProps) => {
    const classes = useStyles();
    const id = mockupEventId(event);
    const status = mockupStatus(event.status);
    const mockup = safeHref(event.mockupUrl);
    const inCart = asFlag(event.toCart);
    const ordered = asFlag(event.orderExist);
    const test = isTestTemplate(event);
    const cartTitle = event.toCartDate ? `Added to cart ${formatDate(event.toCartDate, { time: true })}` : 'Added to cart';
    const canOpenHistory = !!onHistory && asFlag(event.additionalEvents) && event.customerId != null;

    return (
        <article className={classNames(classes.root, { [classes.test]: test }, className)} aria-label={id != null ? `Upload ${id}` : 'Upload'}>
            <div className={classes.media}>
                {mockup ? (
                    <img loading="lazy" src={mockup} alt={id != null ? `Mockup of upload ${id}` : 'Mockup'} className={classes.image} />
                ) : (
                    <div className={classes.placeholder}>
                        <StatusChip tone={status.tone} size="sm">{status.label}</StatusChip>
                        <span>No mockup</span>
                        {id != null && <span>Event {id}</span>}
                    </div>
                )}
                {(inCart || ordered || test) && (
                    <div className={classes.badges}>
                        {inCart && <StatusChip tone="warning" size="sm" title={cartTitle}>Cart</StatusChip>}
                        {ordered && <StatusChip tone="success" size="sm" title="Ordered">Ordered</StatusChip>}
                        {test && <StatusChip tone="danger" size="sm" title="Template ab-test">A/B</StatusChip>}
                    </div>
                )}
                {onInfo && (
                    <IconButton size="small" className={classes.info} aria-label={id != null ? `Details of upload ${id}` : 'Upload details'} onClick={() => onInfo(event)}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
                {canOpenHistory && (
                    <div className={classes.actions}>
                        <Button size="small" variant="contained" onClick={() => onHistory!(event.customerId as string | number)}>History</Button>
                    </div>
                )}
            </div>
            {showTags && <TagStrip tags={event.tags} classes={classes} />}
        </article>
    );
};

export default React.memo(MockupEventCard);
